import Seed from "./Seed";
import Utility from "./Utility";

/**
 * Migration pattern for converting documents coming from different designer versions
 * @module DocumentMigration
 */
export default class DocumentMigration {
    /**
     * Upgrade app data model to the latest
     * @param oldObj Old app data model
     * @returns {*}
     */
    static upgrade(oldObj) {
        var newObj = JSON.parse(JSON.stringify(oldObj));
        switch (oldObj.designer) {
            case "1.0":
                const conv12 = new Convert1_0to2_0();
                newObj = conv12.convert(newObj);
                break;
            case "2.0":
                // Base case since this is the latest version
                return newObj;
            default:
                // Do nothing by default if invalid version
                return newObj;
        }
        // Recursively upgrade until latest version (base case)
        return DocumentMigration.upgrade(newObj);
    }
}

/**
 * @class Convert1_0to2_0
 */
class Convert1_0to2_0 {
    /**
     * Convert port
     * @param {string} portType Designer 1.0 format port name; "hybrid", "in", "out"
     * @returns {string}
     */
    getPort(portType) {
        var portNum = portType.match(/\d+/);
        if (portNum) {
            portNum = parseInt(portNum[0]);
        } else {
            portNum = 0;
        }
        if (portNum === 0 || portNum === 1 || portNum === 2) {
            return "top";
        }
        if (portNum === 3 || portNum === 4 || portNum === 5) {
            return "bottom";
        }
        if (portNum === 6 || portNum === 7 || portNum === 8) {
            return "left";
        }
        if (portNum === 9 || portNum === 10 || portNum === 11) {
            return "right";
        }
    }

    /**
     * Convert link
     * @param {Object} link Designer 1.0 current link model
     * @param {Object} obj Designer 1.0 app model
     * @returns {Object}
     */
    parseLink(link, obj) {
        var l = {
            "id": link.id,
            "_class": "BaseLinkModel",
            "points": link.vertex,
            "source": link.source.node,
            "sourcePort": this.getPort(link.source.port),
            "target": link.target.node,
            "targetPort": this.getPort(link.target.port),
            "props": {
                "config": {
                    "condition": []
                }
            }
        };
        if (link.componentId) {
            if (obj.components[link.componentId]) {
                if (obj.components[link.componentId].type === "INTENT_CATEGORY") {
                    for (var i = 0; i < obj.components[link.componentId].intents.length; i++) {
                        const intent = obj.components[obj.components[link.componentId].intents[i]];
                        if (intent) {
                            l.props.config.condition.push(["out == \"" + intent.name + "\""]);
                        } else {
                            l.props.config.condition.push(["out == \"<UNKNOWN_CLAUSE>\""]);
                        }
                    }
                } else {
                    l.props.config.condition.push(["out == \"" + obj.components[link.componentId].name + "\""]);
                }
            } else {
                l.props.config.condition.push(["out == \"<UNKNOWN_CLAUSE>\""]);
            }
        }
        return l;
    }

    /**
     * Convert module component
     * @param {Object} obj Designer 1.0 current module model
     * @param {Object} root Designer 1.0 app model
     * @returns {Object}
     */
    parseModuleComponent(obj, root) {
        var mod = {
            "id": obj.id,
            "name": obj.name,
            "type": "module",
            "_class": "ModuleNodeModel",
            "props": {
                "iconClass": "package",
                "config": {
                    "description": obj.description
                }
            },
            "version": "1",
            "versions": {
                "1": {
                    "version": 1,
                    "flow": {
                        "offsetX": 0,
                        "offsetY": 0,
                        "zoom": 100,
                        "links": [],
                        "nodes": []
                    }
                }
            },
            "localVariables": []
        };

        var linkCache = {};

        for (var i = 0; i < obj.flowDiagram.length; i++) {
            if (obj.flowDiagram[i].type === "appDiagramming.NodeConnection") {
                const link = this.parseLink(obj.flowDiagram[i], root);
                const key = link.source + "::" + link.target;
                if (!linkCache.hasOwnProperty(key)) {
                    linkCache[key] = link;
                } else if (link.props.config.condition.length > 0) {
                    for (var j = 0; j < link.props.config.condition.length; j++) {
                        linkCache[key].props.config.condition.push(link.props.config.condition[j]);
                    }
                }
            } else {
                const comp = obj.flowDiagram[i].componentId;
                if (comp && root.components[comp]) {
                    mod.versions[mod.version].flow.nodes.push(this.parseNodeComponent({
                        id: obj.flowDiagram[i].id,
                        x: obj.flowDiagram[i].x,
                        y: obj.flowDiagram[i].y,
                        width: obj.flowDiagram[i].width,
                        height: obj.flowDiagram[i].height,
                        name: root.components[comp].name,
                        type: root.components[comp].type
                    }, root.components[comp]));
                } else {
                    const name = obj.flowDiagram[i].type.replace("appDiagramming.", "");
                    if (name === "NoteNode") {
                        mod.versions[mod.version].flow.nodes.push(this.parseNodeComponent({
                            id: obj.flowDiagram[i].id,
                            name: name,
                            x: obj.flowDiagram[i].x,
                            y: obj.flowDiagram[i].y,
                            width: obj.flowDiagram[i].width,
                            height: obj.flowDiagram[i].height,
                            type: "NOTE",
                            note: obj.flowDiagram[i].text
                        }));
                    } else {
                        var type = "UNDEFINED";
                        if (name === "BeginNode") {
                            type = "BEGIN";
                        }
                        if (name === "CommonModuleEndPointNode") {
                            type = "MODULE_END_INTENT";
                        }
                        mod.versions[mod.version].flow.nodes.push(this.parseNodeComponent({
                            id: obj.flowDiagram[i].id,
                            name: name,
                            x: obj.flowDiagram[i].x,
                            y: obj.flowDiagram[i].y,
                            width: obj.flowDiagram[i].width,
                            height: obj.flowDiagram[i].height,
                            type: type,
                            componentId: obj.flowDiagram[i].componentId
                        }));
                    }
                }
            }
        }
        const links = Object.values(linkCache);
        for (var i = 0; i < links.length; i++) {
            mod.versions[mod.version].flow.links.push(links[i]);
        }
        return mod;
    }

    /**
     * Convert node component
     * @param {Object} obj Data object
     * @param {Object} refObj Designer 1.0 current node model
     * @returns {Object}
     */
    parseNodeComponent(obj, refObj = null) {
        var adjustedX = obj.x, adjustedY = obj.y;
        if (obj.x) {
            adjustedX = obj.x + obj.width / 2 - 90;
        }
        if (obj.y) {
            adjustedY = obj.y + obj.height / 2 - 30;
        }

        var model = {
            "id": obj.id,
            "name": obj.name,
            "type": "object",
            "_class": "BaseNodeModel",
            "x": Math.round(adjustedX / 20) * 20,
            "y": Math.round(adjustedY / 20) * 20,
            "props": {
                "config": {}
            }
        };
        model.props.config.description = obj.description;
        if (refObj) {
            model.props.config.description = refObj.description;
            model.props.parentComponentID = refObj.id;
        }
        switch (obj.type) {
            case "MODULE":
                model.type = "module";
                model["_class"] = "ModuleNodeModel";
                if (refObj) {
                    model.props.ref = refObj.id + "::1";
                }
                break;
            case "BEGIN":
                model.name = "Begin";
                model.props.iconClass = "log-in";
                model.props.config.description = "Entry point of the module";
                model["_class"] = "BeginNodeModel";
                break;
            case "PROMPT":
                model.props.iconClass = "message-square";
                model["_class"] = "PromptNodeModel";
                model.props.config.text = refObj ? { "en-US": refObj.text } : { "en-US": obj.text };
                model.props.config.bargeable = refObj ? refObj.bargeable : obj.bargeable;
                model.props.config.repeatable = refObj ? refObj.repeatable : obj.repeatable;
                break;
            //BEN
            case "SAVE_PROMPT":
                model.props.iconClass = "save";
                model["_class"] = "SavePromptNodeModel";
                model.props.config.promptId = refObj ? refObj.prompt : obj.prompt;
                break;
            case "PLAY_SAVED_PROMPT":
                model.props.iconClass = "play";
                model["_class"] = "PlaySavedPromptNodeModel";
                model.props.config.promptId = refObj ? refObj.defaultPrompt : obj.defaultPrompt;
                model.props.config.key = refObj ? refObj.key : obj.key;
                break;
            case "REPORT_TAG":
                var reportType = refObj ? refObj.reportType : obj.reportType;
                model.props.reportType = reportType;
                if (reportType === "BILLING_TAG") {
                    model.props.iconClass = "activity";
                    model["_class"] = "ReportNodeModel";
                    model.props.config.billing = refObj ? refObj.tagData : obj.tagData;
                } else if (reportType === "SCRIPT_PERFORMANCE") {
                    model.props.iconClass = "activity";
                    model["_class"] = "ReportNodeModel";
                    if (obj.tag === "BEGIN") {
                        model.props.config.billing = {
                            key: "START",
                            value: refObj ? refObj.tagData : obj.tagData
                        };

                    }
                    else if (obj.tag === "NONE") {
                        model.props.config.billing = {
                            key: "",
                            value: refObj ? refObj.tagData : obj.tagData
                        };

                    }
                    else {
                        model.props.config.billing = {
                            key: refObj ? refObj.tag : obj.tag,
                            value: refObj ? refObj.tagData : obj.tagData
                        };
                    }
                    //model.props.config.billing = refObj ? refObj.billing : obj.billing;
                }
                else if (reportType === undefined) {
                    model.props.iconClass = "activity";
                    model["_class"] = "ReportNodeModel";
                    if (obj.tag === "BEGIN") {
                        model.props.config.customTag = {
                            key: "START",
                            value: refObj ? refObj.tagData : obj.tagData
                        };

                    }
                    else if (obj.tag === "NONE") {
                        model.props.config.customTag = {
                            key: "",
                            value: refObj ? refObj.tagData : obj.tagData
                        };

                    }
                    else {
                        model.props.config.customTag = {
                            key: refObj ? refObj.tag : obj.tag,
                            value: refObj ? refObj.tagData : obj.tagData
                        };
                    }
                }
                break;
            //END BEN
            case "INPUT":
                model.props.iconClass = "mic";
                model["_class"] = "InputNodeModel";
                model.props.config.globalHandling = refObj ? refObj.globalHandling : obj.globalHandling;
                model.props.config.inputType = refObj ? refObj.inputType : obj.inputType;
                model.props.config.localIntents = refObj ? refObj.localIntents : obj.localIntents;
                model.props.config.expectedIntent = refObj ? refObj.expectedIntent : obj.expectedIntent;
                model.props.config.impliedIntent = refObj ? refObj.impliedIntent : obj.impliedIntent;
                break;
            case "CUSTOM":
                model.props.iconClass = "cpu";
                model["_class"] = "CustomNodeModel";
                model.props.config.functionType = "CUSTOM";
                model.props.config.function = {
                    functionArgument: [],
                    outputs: [],
                    script: ""
                };
                break;
            case "MODULE_END_POINT":
                model.name = "Exit";
                model["_class"] = "ExitNodeModel";
                model.props.iconClass = "log-out";
                model.props.config.outputs = [{
                    argumentName: null,
                    type: "value",
                    value: refObj ? refObj.name : obj.name,
                    varName: null,
                    intent: null
                }];
                break;
            case "MODULE_END_INTENT":
                model.name = "Exit";
                model["_class"] = "ExitNodeModel";
                model.props.iconClass = "log-out";
                model.props.config.outputs = [{
                    argumentName: null,
                    type: "intent",
                    value: null,
                    varName: null,
                    intent: this.tempEndPoint[obj.componentId]
                }];
                break;
            //BEN
            /*
            case "REPORT_TAG":
                model.props.iconClass = "activity";
                model["_class"] = "ReportNodeModel";
                model.props.config.scriptPerformance = {
                    key: refObj ? refObj.tag : obj.tag,
                    value: refObj ? refObj.tagData : obj.tagData
                };
                model.props.config.billing = refObj ? refObj.billing : obj.billing;
                break;
            */
            case "NOTE":
                model["_class"] = "NoteNodeModel";
                model.props.config = {
                    note: obj.note
                };
                break;
            case "VARIABLE":
                model.name = obj.name;
                model.type = "variable";
                model["_class"] = "VariableDataModel";
                model.props = {
                    "iconClass": "hash",
                    "usedIn": [],
                    "config": {
                        "alias": obj.name,
                        "varName": obj.name,
                        "type": "string"
                    }
                };
                break;
            case "INTENT":
                model.type = "intent";
                model.props.iconClass = "radio";
                model["_class"] = "IntentDataModel";
                break;
            case "INTENT_CATEGORY":
                model.type = "intent";
                model["_class"] = "IntentDataModel";
                model.props.iconClass = "tag";
                model.props.nodeTypeName = "Intent (Category)";
                model.props.config = {
                    "intents": obj.intents ? obj.intents : []
                };
                break;
            default:
                break;
        }
        return model;
    }

    /**
     * Convert root module
     * @param {Object} oldRoot Designer 1.0 root module
     * @param {Object} oldObj Designer 1.0 app model
     * @param {string} rootModuleKey Root module key
     * @returns {Object}
     */
    parseRootModule(oldRoot, oldObj, rootModuleKey) {
        var root = {
            "id": rootModuleKey,
            "name": "Start",
            "type": "module",
            "_class": "ModuleNodeModel",
            "props": {},
            "version": "1",
            "versions": {
                "1": {
                    "version": 1,
                    "flow": {
                        "offsetX": 0,
                        "offsetY": 0,
                        "zoom": 100,
                        "links": [],
                        "nodes": []
                    }
                }
            },
            "localVariables": []
        };
        if (oldRoot) {
            const translatedOldRoot = this.parseModuleComponent(oldRoot, oldObj);
            root.versions = JSON.parse(JSON.stringify(translatedOldRoot.versions));
        }
        return root;
    }

    /**
     * Entry point to conversion
     * @param {Object} oldObj Designer 1.0 app model
     * @returns {Object} Designer 2.0 app model
     */
    convert(oldObj) {
        var newObj = Seed.getBlankApp();
        newObj.components.modules = [];
        newObj.client = oldObj.client;
        newObj.application = oldObj.application;
        newObj.version = oldObj.version;
        newObj.designer = "2.0";
        newObj.repoClient = oldObj.client;
        newObj.repoApplication = oldObj.application;
        newObj.repoVersion = oldObj.version;

        newObj.globalIntents = [];
        // Cache for intent jumps
        this.tempIntentHandling = {};
        this.tempIntentHandling = {};
        for (var obj = 0; obj < oldObj.globalInputHandling.length; obj++) {
            if (oldObj.globalInputHandling[obj].type === "GIH_CATEGORY") {
                for (var j = 0; j < oldObj.globalInputHandling[obj].moduleSections.length; j++) {
                    oldObj.globalInputHandling.push(oldObj.globalInputHandling[obj].moduleSections[j]);

                }

            }
        }
        for (var i = 0; i < oldObj.globalInputHandling.length; i++) {
            if (oldObj.globalInputHandling[i].type == "GIH_CATEGORY") {

            }
            else {
                this.tempIntentHandling[oldObj.globalInputHandling[i].intent] = oldObj.globalInputHandling[i].module;
                newObj.globalIntents.push({
                    intent: oldObj.globalInputHandling[i].intent,
                    module: oldObj.globalInputHandling[i].module
                });
            }
        }
        // Parse root module
        const oldRoot = JSON.parse(JSON.stringify(oldObj.components[oldObj.rootModule]));
        delete oldObj.components[oldObj.rootModule];
        newObj.components.modules.push(this.parseRootModule(oldRoot, oldObj, newObj.rootModule));

        // Common module end point to intent
        const commonEndPoint = Seed.getCommonEndPointAsIntent();
        this.tempEndPoint = {};
        for (var id in commonEndPoint) {
            if (!commonEndPoint.hasOwnProperty(id)) {
                continue;
            }
            this.tempEndPoint[id] = commonEndPoint[id].id;
            newObj.components.intents.push(commonEndPoint[id]);
        }

        // Parse components
        const comps = Object.values(oldObj.components);
        for (var i = 0; i < comps.length; i++) {
            const obj = comps[i];
            switch (obj.type) {
                case "VARIABLE":
                    newObj.components.variables.push(this.parseNodeComponent(obj));
                    break;
                case "MODULE":
                    newObj.components.modules.push(this.parseModuleComponent(obj, oldObj));
                    break;
                case "CUSTOM_OUTCOME":  // skip; meaningless in new format as component
                    break;
                case "INTENT_CATEGORY":
                case "INTENT":
                    newObj.components.intents.push(this.parseNodeComponent(obj));
                    break;
                default:
                    newObj.components.objects.push(this.parseNodeComponent(obj));
                    break;
            }
        }
        // Intent two-way reference
        for (var i = 0; i < newObj.components.intents.length; i++) {
            const int = newObj.components.intents[i];
            if (int.props.iconClass === "tag") {
                for (var c = 0; c < int.props.config.intents.length; c++) {
                    const cIndex = newObj.components.intents.findIndex(elem => elem.id === int.props.config.intents[c]);
                    if (cIndex !== -1) {
                        const cint = newObj.components.intents[cIndex];
                        if (!cint.props.config.categories) {
                            cint.props.config.categories = [];
                        }
                        cint.props.config.categories.push(int.id);
                    }
                }
            }
        }
        // Module begin node
        newObj.components.objects.push({
            "id": Utility.uuid4(),
            "name": "Begin",
            "type": "object",
            "_class": "BeginNodeModel",
            "props": {
                "iconClass": "log-in",
                "config": {}
            }
        });
        // Locale
        var availableLangs = [];
        if (oldObj.alternateLanguages) {
            for (var i = 0; i < oldObj.alternateLanguages.length; i++) {
                const lang = oldObj.alternateLanguages[i];
                const langName = lang.name.toUpperCase();
                availableLangs.push(langName);
                if (lang.promptText) {
                    for (var pid in lang.promptText) {
                        if (!lang.promptText.hasOwnProperty(pid)) {
                            continue;
                        }
                        const pIdx = newObj.components.objects.findIndex(elem => elem.id === pid);
                        if (pIdx !== -1) {
                            const pNode = newObj.components.objects[pIdx];
                            if (pNode.props.config && pNode.props.config.text) {
                                pNode.props.config.text[langName] = lang.promptText[pid];
                            }
                        }
                    }
                }
            }
        }
        if (availableLangs.length === 0) {
            availableLangs = ["en-US"];
        }
        if (availableLangs.indexOf("en-US") === -1) {
            availableLangs = ["en-US", ...availableLangs];
        }
        newObj.languages = [{
            "language": "en-US",
            "voice": "Mari"
        }];
        //newObj.language = availableLangs[0];
        return newObj;
    }
}
